export const EVENT_STATUS_OPTIONS = ["Draft", "Scheduled", "Selling", "Live", "Completed"];

const LABELS = {
  draft: "Draft",
  scheduled: "Scheduled",
  selling: "Selling",
  live: "Live",
  completed: "Completed",
};

export function normalizeEventStatus(raw) {
  const key = String(raw || "draft").trim().toLowerCase();
  return LABELS[key] ? key : "draft";
}

function parseDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const d = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const d = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function resolveEventPublicAccess(event, now = new Date()) {
  if (event?.globalDemo) {
    return {
      storedStatus: "live",
      effectiveStatus: "live",
      siteVisible: true,
      registrationOpen: true,
      message: null,
    };
  }

  const stored = normalizeEventStatus(event?.status);
  const start = parseDate(event?.startDate);
  const end = parseDate(event?.endDate);
  const effectiveEnd = end || start;
  const publishAt = parseDateTime(event?.publishAt);
  const today = startOfDay(now);

  const afterEvent = Boolean(effectiveEnd && today > startOfDay(effectiveEnd));
  const duringEvent = Boolean(
    start &&
      effectiveEnd &&
      today >= startOfDay(start) &&
      today <= startOfDay(effectiveEnd),
  );
  const beforeEvent = Boolean(start && today < startOfDay(start));

  const blocked = (message, effectiveStatus = stored) => ({
    storedStatus: stored,
    effectiveStatus: effectiveStatus,
    siteVisible: false,
    registrationOpen: false,
    message,
  });

  const completed = (message = "This event has ended.") => ({
    storedStatus: stored,
    effectiveStatus: "completed",
    siteVisible: true,
    registrationOpen: false,
    message,
  });

  if (stored === "draft") return blocked("This event is not published yet.");
  if (afterEvent) return completed();

  if (stored === "scheduled") {
    if (!publishAt) return blocked("Set a publish date before scheduling this event.");
    if (now < publishAt) {
      return blocked(`This event opens on ${publishAt.toLocaleString()}.`);
    }
    if (duringEvent) {
      return {
        storedStatus: stored,
        effectiveStatus: "live",
        siteVisible: true,
        registrationOpen: false,
        message: "Registration is closed during the live event.",
      };
    }
    return {
      storedStatus: stored,
      effectiveStatus: "scheduled",
      siteVisible: true,
      registrationOpen: true,
      message: null,
    };
  }

  if (stored === "selling") {
    return {
      storedStatus: stored,
      effectiveStatus: duringEvent ? "live" : "selling",
      siteVisible: true,
      registrationOpen: true,
      message: null,
    };
  }

  if (stored === "live") {
    if (beforeEvent && start) {
      return {
        storedStatus: stored,
        effectiveStatus: "selling",
        siteVisible: true,
        registrationOpen: true,
        message: null,
      };
    }
    return {
      storedStatus: stored,
      effectiveStatus: "live",
      siteVisible: true,
      registrationOpen: false,
      message: null,
    };
  }

  if (stored === "completed") return completed();

  return blocked("This event is not available.");
}

export function getEventPublicAccess(event) {
  if (event?.publicAccess) {
    return {
      storedStatus: event.publicAccess.storedStatus || normalizeEventStatus(event?.status),
      effectiveStatus: event.publicAccess.effectiveStatus || normalizeEventStatus(event?.status),
      siteVisible: Boolean(event.publicAccess.siteVisible),
      registrationOpen: Boolean(event.publicAccess.registrationOpen),
      message: event.publicAccess.message || null,
    };
  }
  return resolveEventPublicAccess(event);
}

export function isSiteVisible(event) {
  return getEventPublicAccess(event).siteVisible;
}

export function isRegistrationOpen(event) {
  return getEventPublicAccess(event).registrationOpen;
}
